import { useState, useMemo } from 'react'
import { useParams, Navigate } from 'react-router-dom'
import {
  PromptsHeader,
  PromptsStatsCards,
  PromptPhaseSection,
  CreatePromptDialog,
  DeletePromptDialog,
} from '@/components/projects'
import { useAuth, useProjectPhases, usePrompts, usePromptStats } from '@/hooks'
import { Permissions } from '@/lib/permissions'
import type { Prompt } from '@/types'

export function PromptsPage() {
  const { projectId } = useParams()
  const { hasPermission, isLoading: authLoading } = useAuth(projectId)
  const { data: projectPhases = [] } = useProjectPhases(projectId!)
  const { data: prompts = [] } = usePrompts(projectId!)
  const { data: stats, isLoading: statsLoading } = usePromptStats(projectId!)

  const [createPromptOpen, setCreatePromptOpen] = useState(false)
  const [deletingPrompt, setDeletingPrompt] = useState<Prompt | null>(null)

  const promptsByProjectPhase = useMemo(() => {
    const grouped = new Map<string, Prompt[]>()
    for (const projectPhase of projectPhases) {
      grouped.set(projectPhase.id, [])
    }
    for (const prompt of prompts) {
      const list = grouped.get(prompt.projectPhaseId)
      if (list) {
        list.push(prompt)
      }
    }
    return grouped
  }, [projectPhases, prompts])

  if (authLoading) {
    return null
  }

  if (!hasPermission(Permissions.Project.Prompt.Read, projectId)) {
    return <Navigate to={`/projects/${projectId}/backlog`} replace />
  }

  return (
    <div className="space-y-6">
      <PromptsHeader projectId={projectId!} onCreatePrompt={() => setCreatePromptOpen(true)} />
      <PromptsStatsCards stats={stats} isLoading={statsLoading} />
      <div className="space-y-4">
        {projectPhases.map((projectPhase) => (
          <PromptPhaseSection
            key={projectPhase.id}
            projectPhase={projectPhase}
            prompts={promptsByProjectPhase.get(projectPhase.id) ?? []}
            onDelete={setDeletingPrompt}
          />
        ))}
      </div>

      <CreatePromptDialog
        projectId={projectId!}
        open={createPromptOpen}
        onOpenChange={setCreatePromptOpen}
      />

      {deletingPrompt && (
        <DeletePromptDialog
          projectId={projectId!}
          prompt={deletingPrompt}
          open={deletingPrompt !== null}
          onOpenChange={(open) => { if (!open) setDeletingPrompt(null) }}
        />
      )}
    </div>
  )
}
